/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */

/**
 * FEATURE SUMMARY: Order History Overview
 * 
 * This component provides a 360-degree view of a specific order in the Prakruti Admin Panel.
 * 
 * CORE FUNCTIONALITIES:
 * 1. Data Analytics: Real-time billing calculations including net amounts, discounts, and pending balances.
 * 2. Modular Architecture: Separated into logical sub-components (OrderInfo, PaymentSummary, GuestDetails, RoomDetails, History).
 * 3. Order Tracking: Deduplicated history timeline showing payment progression and status transitions.
 * 4. Room Management: Detailed view of room occupancy with specialized UI for room upgrades.
 * 5. Document Generation: Integrated jsPDF utility for downloading order summaries and payment receipts.
 */

import { Row, Col, Spin } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';

// Components
import Sidebar from '../../../Sidebar';
import Header from '../../../Header';
import { Breadcrumb } from '../../../Card/Cards';
import OrderInfoCard from './components/OrderInfoCard';
import PaymentSummaryCard from './components/PaymentSummaryCard';
import GuestDetailsCard from './components/GuestDetailsCard';
import RoomDetailsCard from './components/RoomDetailsCard';
import OrderHistoryCard from './components/OrderHistoryCard';

// Hooks & Utils
import { useOrderHistoryLogic } from './hooks/useOrderHistoryLogic';
import { generateOrderPDF } from './utils/pdfGenerator';

const OrderHistoryOverView = () => {
  const {
    order,
    loader,
    billing,
    breadcrumbList,
    hasPatientDetails,
    hasAppointmentDetails,
    dedupedHistory,
    id
  } = useOrderHistoryLogic();

  const handleDownloadPDF = () => {
    generateOrderPDF(order, billing, dedupedHistory, id);
  };

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div id="order-history-content" className="content" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
          <Spin spinning={loader} size="large">
            <div className="d-flex justify-content-between align-items-center">
              <div style={{ flex: 1 }}>
                <Breadcrumb page={breadcrumbList} />
              </div>
              <div className="me-4 mt-3">
                <button
                  id="download-pdf-btn"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={handleDownloadPDF}
                >
                  <FeatherIcon icon="download" size={16} />
                  Download PDF
                </button>
              </div>
            </div>

            {/* Main Container - Two Column Layout */}
            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 16px' }}>
              <Row gutter={[24, 24]}>
                {/* Left Column - Detailed Information */}
                <Col xs={24} lg={16}>
                  <OrderInfoCard
                    order={order}
                    hasPatientDetails={hasPatientDetails}
                    hasAppointmentDetails={hasAppointmentDetails}
                  />

                  <GuestDetailsCard guestDetails={order?.additionalInfo?.guestDetails} />

                  <RoomDetailsCard roomDetails={order?.additionalInfo?.roomDetails} />
                </Col>

                {/* Right Column - Financials & Timeline */}
                <Col xs={24} lg={8}>
                  <PaymentSummaryCard
                    order={order}
                    billing={billing}
                  />

                  <OrderHistoryCard
                    dedupedHistory={dedupedHistory}
                    finalAmount={billing?.netAmount || 0}
                  />
                </Col>
              </Row>
            </div>
          </Spin>
        </div>
      </div>
    </>
  );
};

export default OrderHistoryOverView;
